import { Box, Collapse, Grid } from "@mui/material";
import { observer } from "mobx-react";
import React, { useState } from "react";
import AcxButton from "components/UI/AcxButton";
import AcxInputLabel from "components/UI/AcxInputLabel";
import {
    DateRangeFilter,
    HierarchiesFilter,
} from "components/ApplicationFilters/FiltersAndChips/BaseFilterComponents";
import { FilterComponent } from "components/ApplicationFilters/FiltersAndChips/BaseFilterComponents/FilterComponent";
import {
    ApplicationFilterItemsStore,
    FilterKey,
} from "stores/ApplicationFilters/ApplicationFilterItemsStore";
import { ApplicationFiltersInstanceStore } from "stores/ApplicationFilters/ApplicationFiltersInstanceStore";
import { useStore } from "utils/useStore";

const additionalFilters: { key: FilterKey; label?: string }[] = [
    { key: "callDuration", label: "Call Duration" },
    { key: "mediaTypes", label: "Media Types" },
    { key: "evaluationTypes", label: "Evaluation Types" },
    { key: "agentIds", label: "Agents" },
    { key: "favoriteConversations", label: "Favorite Conversations" },
    { key: "eddyEffectStatus", label: "Eddy Effect" },
    { key: "hipaaCompliance", label: "HIPAA Compliance" },
    { key: "beginningSentiment", label: "Beginning Sentiment" },
    { key: "endingSentiment", label: "Ending Sentiment" },
    { key: "safetyEvent", label: "Safety Event" },
    { key: "contactTypes", label: "Contact Types" },
    { key: "patientJourneyStages", label: "Patient Journey Stages" },
    { key: "modelResults" },
    { key: "questionResults", label: "Question Results" },
    // The two Theme filters share this label and are told apart by a "(Deprecated)" marker added at
    // render time, for the viewers who can see that status. See
    // ApplicationFilterItemsStore.withDeprecationMarker.
    { key: "topics", label: "Primary Theme" },
    { key: "themes", label: "Primary Theme" },
    { key: "primaryTopics", label: "Primary Topic" },
    { key: "rbcFilterItems" },
    { key: "wordsAndPhrasesSearchString" },
    { key: "dataLabels", label: "Data Labeling" },
];

type ConversationFiltersProps = {
    filters: ApplicationFiltersInstanceStore;
    /**
     * Lay date range and hierarchies out in two columns that fill the row.
     */
    fullWidth?: boolean;
};

/**
 * Date range and hierarchy filters, with a button that reveals the rest.
 */
export const ConversationFilters = observer(function ({
    filters,
    fullWidth,
}: ConversationFiltersProps) {
    const applicationFilterItemsStore = useStore(ApplicationFilterItemsStore);
    const [showMoreFilters, setShowMoreFilters] = useState(false);

    const dateRange = (
        <DateRangeFilter
            filterKey="dateRange"
            store={filters}
            {...(fullWidth
                ? {
                      maxWidth: "100%",
                      wrapperStyle: { maxWidth: "100%" },
                      inputStyle: { width: "100%" },
                  }
                : {})}
        />
    );
    const showMoreButton = (
        <AcxButton
            onClick={() => setShowMoreFilters(!showMoreFilters)}
            variant="outlined"
        >
            {showMoreFilters ? "Show less" : "Show more filters"}
        </AcxButton>
    );
    const hierarchies = (
        <HierarchiesFilter
            filterKey="hierarchyIds"
            store={filters}
            popperPlacement="right-start"
            {...(fullWidth ? { width: "100%" } : {})}
        />
    );

    return (
        <Grid container item direction="column" flexWrap="nowrap" rowGap={1}>
            {fullWidth ? (
                <Box display="flex" columnGap={2} width="100%">
                    <Box flex={1} minWidth={0}>
                        <AcxInputLabel>Date range</AcxInputLabel>
                        {dateRange}
                    </Box>
                    <Box flex={1} minWidth={0}>
                        <AcxInputLabel>Hierarchies</AcxInputLabel>
                        {hierarchies}
                    </Box>
                </Box>
            ) : (
                <Grid container item direction="row" columnGap={1}>
                    <Grid item>
                        Date Range
                        {dateRange}
                    </Grid>
                    <Grid item>
                        Hierarchies
                        {hierarchies}
                    </Grid>
                </Grid>
            )}
            <Collapse in={showMoreFilters} timeout="auto" unmountOnExit>
                <Grid
                    container
                    item
                    direction="column"
                    flexWrap="nowrap"
                    rowGap={2}
                    sx={() => ({ maxWidth: "50%" })}
                >
                    {additionalFilters.map((filter) => (
                        <Grid item key={filter.key}>
                            {filter.label &&
                                applicationFilterItemsStore.withDeprecationMarker(
                                    filter.key,
                                    filter.label,
                                )}
                            <FilterComponent
                                filterKey={filter.key}
                                store={filters}
                            />
                        </Grid>
                    ))}
                </Grid>
            </Collapse>
            {/* A grid item keeps the button its own width instead of stretching */}
            {fullWidth ? <Grid item>{showMoreButton}</Grid> : showMoreButton}
        </Grid>
    );
});
